if (!customElements.get("cm-collection")) {
  class CmCollection extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.collectionUrl = new URL(
        this.dataset.collectionUrl,
        window.location.origin,
      );
      this.defaultSort = this.dataset.defaultSort || "";
      this.requestId = 0;
      this.fetchController = null;
      this.eventController = null;
      this.closeTimer = null;
      this.lastTrigger = null;
      this.reducedMotionQuery = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleDialogCancel = this.handleDialogCancel.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
    }

    connectedCallback() {
      if (this.eventController) return;

      this.setAttribute("data-enhanced", "");
      this.syncCharacterContext(this, new URL(window.location.href));
      this.dialog = this.querySelector("[data-cm-filter-drawer]");
      this.eventController = new AbortController();
      const { signal } = this.eventController;

      this.addEventListener("click", this.handleClick, { signal });
      this.addEventListener("change", this.handleChange, { signal });
      this.addEventListener("submit", this.handleSubmit, { signal });
      window.addEventListener("popstate", this.handlePopState, { signal });

      if (this.dialog) {
        this.dialog.addEventListener("cancel", this.handleDialogCancel, {
          signal,
        });
        this.dialog.addEventListener("close", this.handleDialogClose, {
          signal,
        });
      }
    }

    disconnectedCallback() {
      this.eventController?.abort();
      this.eventController = null;
      this.fetchController?.abort();
      this.fetchController = null;
      window.clearTimeout(this.closeTimer);
    }

    handleClick(event) {
      const openButton = event.target.closest("[data-cm-filter-open]");
      if (openButton) {
        event.preventDefault();
        this.openDrawer(openButton);
        return;
      }

      const closeButton = event.target.closest("[data-cm-filter-close]");
      if (closeButton) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.target === this.dialog) {
        const panel = this.dialog.querySelector("[data-cm-filter-panel]");
        const { left, right, top, bottom } = panel.getBoundingClientRect();
        const clickedOutsidePanel =
          event.clientX < left ||
          event.clientX > right ||
          event.clientY < top ||
          event.clientY > bottom;

        if (clickedOutsidePanel) this.closeDrawer();
        return;
      }

      const disclosureButton = event.target.closest(
        "[data-cm-disclosure-toggle]",
      );
      if (disclosureButton && this.contains(disclosureButton)) {
        event.preventDefault();
        this.toggleDisclosure(disclosureButton);
        return;
      }

      const link = event.target.closest("a[data-cm-collection-link]");
      if (!link || !this.shouldHandleLink(event, link)) return;

      const targetUrl = new URL(link.href, window.location.href);
      if (targetUrl.origin !== window.location.origin) return;

      event.preventDefault();
      const isPagination = link.hasAttribute("data-cm-pagination-link");
      this.render(targetUrl, {
        push: true,
        focusResults: isPagination,
      });
    }

    handleChange(event) {
      if (event.target.matches("[data-cm-sort]")) {
        const form = event.target.closest("[data-cm-sort-form]");
        this.render(this.urlFromForm(form), { push: true });
        return;
      }

      if (
        event.target.matches("[data-cm-filter-control]") ||
        event.target.matches("[data-cm-price-input]")
      ) {
        const form = event.target.closest("[data-cm-filter-form]");

        this.render(this.urlFromForm(form), { push: true });
      }
    }

    handleSubmit(event) {
      const sortForm = event.target.closest("[data-cm-sort-form]");
      if (sortForm) {
        event.preventDefault();
        this.render(this.urlFromForm(sortForm), { push: true });
        return;
      }

      const filterForm = event.target.closest("[data-cm-filter-form]");
      if (filterForm) {
        event.preventDefault();
        this.render(this.urlFromForm(filterForm), {
          push: true,
          closeAfter: true,
        });
      }
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.closeDrawer();
    }

    handleDialogClose() {
      this.dialog?.removeAttribute("data-open");
      this.setTriggerState(false);
    }

    handlePopState() {
      if (window.location.pathname !== this.collectionUrl.pathname) {
        window.location.reload();
        return;
      }

      this.render(window.location.href, { push: false });
    }

    shouldHandleLink(event, link) {
      return (
        !event.defaultPrevented &&
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey &&
        !link.hasAttribute("download") &&
        (!link.target || link.target === "_self")
      );
    }

    urlFromForm(form) {
      const url = new URL(form.action, window.location.origin);
      const formData = new FormData(form);

      for (const [name, value] of formData.entries()) {
        if (String(value).trim() !== "") {
          url.searchParams.append(name, value);
        }
      }

      if (
        this.defaultSort &&
        url.searchParams.get("sort_by") === this.defaultSort
      ) {
        url.searchParams.delete("sort_by");
      }

      url.searchParams.delete("page");
      return url;
    }

    syncCharacterContext(root, url) {
      const parameter = "filter.p.m.custom.characters";
      const values = url.searchParams.getAll(parameter).filter(Boolean);
      const label = values.length ? url.searchParams.get("cm_character")?.trim() : "";
      const activeFilters = root.querySelector("[data-cm-active-filters]");
      const primaryChip = root.querySelector("[data-cm-primary-chip]");
      const heading = root.querySelector("[data-cm-collection-title]");
      const characterName = values.length ? label || primaryChip?.dataset.cmCharacterLabel : "";
      const primaryTitle = characterName || heading?.dataset.defaultTitle || "";

      if (heading) heading.textContent = primaryTitle;
      if (primaryChip) primaryChip.textContent = primaryTitle;
      const description = root.querySelector("[data-cm-collection-description]");
      if (description) description.hidden = Boolean(characterName);

      root.querySelectorAll("[data-cm-sort-form], [data-cm-filter-form]").forEach((form) => {
        form.querySelectorAll(`input[name="${parameter}"], input[name="cm_character"]`).forEach((input) => input.remove());
        const state = form.querySelector("[data-cm-filter-state]") || form;
        const fields = values.map((value) => [parameter, value]);
        if (characterName) fields.push(["cm_character", characterName]);
        fields.forEach(([name, value]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = name;
          input.value = value;
          state.append(input);
        });
      });

      root.querySelectorAll("a[data-cm-collection-link]").forEach((link) => {
        const target = new URL(link.getAttribute("href"), url);
        if (target.origin !== url.origin || target.pathname !== url.pathname) return;
        target.searchParams.delete(parameter);
        values.forEach((value) => target.searchParams.append(parameter, value));
        if (characterName) target.searchParams.set("cm_character", characterName);
        else target.searchParams.delete("cm_character");
        link.href = `${target.pathname}${target.search}`;
      });

      const count = activeFilters?.querySelectorAll("a.cm-active-filters__chip").length || 0;
      root.querySelectorAll("[data-cm-filter-count], [data-cm-filter-heading-count]").forEach((badge) => {
        badge.textContent = badge.hasAttribute("data-cm-filter-heading-count") ? `(${count})` : String(count);
        badge.hidden = count === 0;
      });
      const hasFilters = Array.from(url.searchParams.entries()).some(([name, value]) => name.startsWith("filter.") && name !== parameter && value.trim());
      root.querySelectorAll("[data-cm-empty-filtered]").forEach((element) => { element.hidden = !hasFilters; });
      root.querySelectorAll("[data-cm-empty-unfiltered]").forEach((element) => { element.hidden = hasFilters; });
    }

    openDrawer(trigger) {
      if (!this.dialog || this.dialog.open) return;

      window.clearTimeout(this.closeTimer);
      this.lastTrigger = trigger;
      this.dialog.showModal();
      this.setTriggerState(true);

      window.requestAnimationFrame(() => {
        if (!this.dialog?.open) return;
        this.dialog.setAttribute("data-open", "true");
        this.querySelector("[data-cm-filter-close]")?.focus({
          preventScroll: true,
        });
      });
    }

    closeDrawer({ restoreFocus = true } = {}) {
      if (!this.dialog?.open) return;

      window.clearTimeout(this.closeTimer);
      this.dialog.removeAttribute("data-open");
      this.setTriggerState(false);

      const finishClose = () => {
        if (this.dialog?.open) this.dialog.close();

        if (restoreFocus) {
          const currentTrigger = this.querySelector("[data-cm-filter-open]");
          const returnTarget =
            currentTrigger || (this.lastTrigger?.isConnected && this.lastTrigger);
          returnTarget?.focus({ preventScroll: true });
        }
      };

      if (this.reducedMotionQuery.matches) {
        finishClose();
      } else {
        this.closeTimer = window.setTimeout(finishClose, 270);
      }
    }

    setTriggerState(isOpen) {
      const trigger = this.querySelector("[data-cm-filter-open]");
      trigger?.setAttribute("aria-expanded", String(isOpen));
    }

    toggleDisclosure(toggle) {
      const panelId = toggle.getAttribute("aria-controls");
      const panel = panelId ? document.getElementById(panelId) : null;
      if (!panel || !this.contains(panel)) return;

      const shouldOpen = toggle.getAttribute("aria-expanded") !== "true";
      this.setDisclosureState(toggle, panel, shouldOpen);
    }

    setDisclosureState(toggle, panel, isOpen) {
      toggle.setAttribute("aria-expanded", String(isOpen));
      panel.hidden = !isOpen;
    }

    async render(target, options = {}) {
      const {
        push = true,
        closeAfter = false,
        focusResults = false,
      } = options;
      const targetUrl = new URL(target, window.location.href);
      targetUrl.hash = "";

      if (targetUrl.origin !== window.location.origin) {
        window.location.assign(targetUrl.href);
        return;
      }

      const stateUrl = `${targetUrl.pathname}${targetUrl.search}`;
      const currentUrl = `${window.location.pathname}${window.location.search}`;

      if (push && stateUrl === currentUrl) {
        if (this.fetchController) {
          this.fetchController.abort();
          this.fetchController = null;
          this.requestId += 1;
          this.setLoading(false);
        }
        if (closeAfter) this.closeDrawer();
        return;
      }

      const viewState = this.captureViewState();
      this.fetchController?.abort();
      this.fetchController = new AbortController();
      const activeController = this.fetchController;
      const requestId = ++this.requestId;
      const sectionUrl = new URL(targetUrl);
      sectionUrl.searchParams.set("section_id", this.sectionId);

      this.setLoading(true);

      try {
        const response = await fetch(sectionUrl, {
          signal: activeController.signal,
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (!response.ok) {
          throw new Error(`Collection section request failed: ${response.status}`);
        }

        const markup = await response.text();
        if (requestId !== this.requestId) return;

        const parsedDocument = new DOMParser().parseFromString(
          markup,
          "text/html",
        );
        const nextCollection = parsedDocument.querySelector("cm-collection");
        if (
          !nextCollection ||
          nextCollection.dataset.sectionId !== this.sectionId
        ) {
          throw new Error("Collection section markup was incomplete.");
        }

        this.syncCharacterContext(nextCollection, targetUrl);
        this.applyResponse(nextCollection, viewState);

        if (push && stateUrl !== currentUrl) {
          window.history.pushState({ cmCollection: true }, "", stateUrl);
        }

        this.announce(
          nextCollection.querySelector("[data-cm-status-message]")?.textContent,
        );

        if (focusResults) {
          this.focusResults();
        }

        if (closeAfter) {
          this.closeDrawer();
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        window.location.assign(targetUrl.href);
      } finally {
        if (requestId === this.requestId) {
          this.fetchController = null;
          this.setLoading(false);
        }
      }
    }

    captureViewState() {
      const activeElement = document.activeElement;
      const focusId =
        activeElement instanceof HTMLElement && this.contains(activeElement)
          ? activeElement.id
          : "";
      const focusWasInActiveFilters = Boolean(
        activeElement?.closest?.("[data-cm-active-filters]"),
      );
      const focusControl =
        activeElement instanceof HTMLInputElement &&
        activeElement.matches(
          "[data-cm-filter-control], [data-cm-price-input]",
        )
          ? {
              name: activeElement.name,
              value: activeElement.value,
            }
          : null;
      const disclosureStates = new Map();

      if (this.dialog?.open) {
        this.querySelectorAll(
          "[data-cm-filter-groups] [data-cm-disclosure-toggle][id]",
        ).forEach((toggle) => {
          disclosureStates.set(
            toggle.id,
            toggle.getAttribute("aria-expanded") === "true",
          );
        });
      }

      return {
        drawerOpen: Boolean(this.dialog?.open),
        filterScroll:
          this.querySelector("[data-cm-filter-groups]")?.scrollTop || 0,
        focusId,
        focusControl,
        focusWasInActiveFilters,
        disclosureStates,
      };
    }

    applyResponse(nextCollection, viewState) {
      const selectors = [
        "[data-cm-collection-header]",
        "[data-cm-toolbar]",
        "[data-cm-results]",
        "[data-cm-filter-state]",
        "[data-cm-filter-heading]",
        "[data-cm-filter-header-actions]",
        "[data-cm-filter-groups]",
        "[data-cm-filter-footer]",
      ];

      selectors.forEach((selector) => {
        this.syncRegion(selector, nextCollection);
      });

      for (const [id, wasOpen] of viewState.disclosureStates) {
        const toggle = document.getElementById(id);
        const panelId = toggle?.getAttribute("aria-controls");
        const panel = panelId ? document.getElementById(panelId) : null;

        if (toggle && panel && this.contains(toggle) && this.contains(panel)) {
          this.setDisclosureState(toggle, panel, wasOpen);
        }
      }

      const filterGroups = this.querySelector("[data-cm-filter-groups]");
      if (filterGroups && viewState.drawerOpen) {
        filterGroups.scrollTop = viewState.filterScroll;
        this.setTriggerState(true);
      }

      if (viewState.focusId) {
        const focusTarget = document.getElementById(viewState.focusId);
        if (
          focusTarget &&
          this.contains(focusTarget) &&
          !focusTarget.disabled
        ) {
          focusTarget.focus({ preventScroll: true });
          return;
        }
      }

      if (viewState.focusControl) {
        const filterControls = this.querySelectorAll(
          "[data-cm-filter-control], [data-cm-price-input]",
        );
        const exactControl = Array.from(filterControls).find(
          (control) =>
            control.name === viewState.focusControl.name &&
            control.value === viewState.focusControl.value,
        );
        const namedControl =
          exactControl ||
          Array.from(filterControls).find(
            (control) => control.name === viewState.focusControl.name,
          );

        if (namedControl && !namedControl.disabled) {
          namedControl.focus({ preventScroll: true });
          return;
        }
      }

      if (viewState.focusWasInActiveFilters) {
        const fallback =
          this.querySelector("[data-cm-active-filters] a:not([hidden])") ||
          this.querySelector("[data-cm-filter-open]");
        fallback?.focus({ preventScroll: true });
      }
    }

    syncRegion(selector, nextCollection) {
      const currentRegion = this.querySelector(selector);
      const nextRegion = nextCollection.querySelector(selector);

      if (!currentRegion && !nextRegion) return;
      if (!currentRegion || !nextRegion) {
        throw new Error(`Collection region missing: ${selector}`);
      }

      currentRegion.innerHTML = nextRegion.innerHTML;
      currentRegion.toggleAttribute(
        "hidden",
        nextRegion.hasAttribute("hidden"),
      );
    }

    setLoading(isLoading) {
      this.toggleAttribute("data-loading", isLoading);
      this.querySelector("[data-cm-results]")?.setAttribute(
        "aria-busy",
        String(isLoading),
      );
    }

    announce(message) {
      const status = this.querySelector("[data-cm-status]");
      if (!status || !message) return;

      status.textContent = "";
      window.requestAnimationFrame(() => {
        status.textContent = message.trim();
      });
    }

    focusResults() {
      const results = this.querySelector("[data-cm-results]");
      if (!results) return;

      results.focus({ preventScroll: true });
      results.scrollIntoView({
        behavior: this.reducedMotionQuery.matches ? "auto" : "smooth",
        block: "start",
      });
    }
  }

  customElements.define("cm-collection", CmCollection);
}
